import React from 'react'
import Icon from 'blocks.simple/icon/icon'
import translate from 'core/translate'
import { cn } from 'ethcss'
import { TemplateElement } from '../templateEditor.types'
import { getLayerPlacement, LayerPlacement, LayerRow } from './templateEditor__buildLayerRows'
import styles from './templateEditor__leftPanels.jcss'

const GROUP_CHILD_MIME = 'application/x-group-child'

type GroupDrag = {
    groupId: string
    fromVisual: number
}

type GroupDragOver = {
    groupId: string
    visual: number
}

export type LayerRowDrag = {
    fromIndex: number | null
    overIndex: number | null
    groupDrag: GroupDrag | null
    groupDragOver: GroupDragOver | null
    setFromIndex: React.Dispatch<React.SetStateAction<number | null>>
    setOverIndex: React.Dispatch<React.SetStateAction<number | null>>
    setGroupDrag: React.Dispatch<React.SetStateAction<GroupDrag | null>>
    setGroupDragOver: React.Dispatch<React.SetStateAction<GroupDragOver | null>>
    clear: () => void
    onReorder: (fromIndex: number, toIndex: number) => void
    onReorderGroup: (groupId: string, fromVisualIndex: number, toVisualIndex: number) => void
}

type LayerRowItemProps = {
    row: LayerRow
    selected: boolean
    expanded: boolean
    parentHidden: boolean
    drag: LayerRowDrag
    onSelect: (id: string, additive: boolean) => void
    onToggleHidden: (id: string) => void
    onToggleLock: (id: string) => void
    onToggleExpanded: (groupId: string) => void
    onContextMenu: (elementId: string, event: React.MouseEvent) => void
}

type DropHandlers = {
    onDragOver: (event: React.DragEvent) => void
    onDragLeave: (event: React.DragEvent) => void
    onDrop: (event: React.DragEvent) => void
}

const isLeavingTarget = (event: React.DragEvent) => !event.currentTarget.contains(event.relatedTarget as Node | null)

const allowMove = (event: React.DragEvent) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
}

const parseGroupChildPayload = (raw: string): GroupDrag | null => {
    try {
        return JSON.parse(raw) as GroupDrag
    } catch {
        return null
    }
}

const isRowDragging = (placement: LayerPlacement, drag: LayerRowDrag) => {
    if (placement.kind === 'top') {
        return drag.fromIndex === placement.index
    }
    if (placement.kind === 'child') {
        return drag.groupDrag?.groupId === placement.groupId && drag.groupDrag.fromVisual === placement.visual
    }
    return false
}

const isRowDropTarget = (placement: LayerPlacement, drag: LayerRowDrag) => {
    if (placement.kind === 'top') {
        return drag.overIndex === placement.index && drag.fromIndex !== null && drag.fromIndex !== placement.index
    }
    if (placement.kind === 'child') {
        return (
            drag.groupDrag?.groupId === placement.groupId &&
            drag.groupDragOver?.groupId === placement.groupId &&
            drag.groupDragOver.visual === placement.visual &&
            drag.groupDrag.fromVisual !== placement.visual
        )
    }
    return false
}

const startRowDrag = (placement: LayerPlacement, drag: LayerRowDrag, event: React.DragEvent) => {
    event.stopPropagation()
    event.dataTransfer.effectAllowed = 'move'

    if (placement.kind === 'top') {
        event.dataTransfer.setData('text/plain', String(placement.index))
        drag.setFromIndex(placement.index)
        drag.setOverIndex(placement.index)
        return
    }

    if (placement.kind === 'child') {
        event.dataTransfer.setData(
            GROUP_CHILD_MIME,
            JSON.stringify({ groupId: placement.groupId, fromVisual: placement.visual })
        )
        drag.setGroupDrag({ groupId: placement.groupId, fromVisual: placement.visual })
        drag.setGroupDragOver({ groupId: placement.groupId, visual: placement.visual })
    }
}

const getTopDropHandlers = (index: number, drag: LayerRowDrag): DropHandlers => ({
    onDragOver: (event) => {
        allowMove(event)
        if (drag.overIndex !== index) {
            drag.setOverIndex(index)
        }
    },
    onDragLeave: (event) => {
        if (isLeavingTarget(event)) {
            drag.setOverIndex((current) => (current === index ? null : current))
        }
    },
    onDrop: (event) => {
        event.preventDefault()
        const fromIndex = Number.parseInt(event.dataTransfer.getData('text/plain'), 10)
        drag.clear()
        if (!Number.isFinite(fromIndex) || fromIndex === index) {
            return
        }
        drag.onReorder(fromIndex, index)
    },
})

const getChildDropHandlers = (groupId: string, visual: number, drag: LayerRowDrag): DropHandlers => ({
    onDragOver: (event) => {
        allowMove(event)
        if (!drag.groupDragOver || drag.groupDragOver.groupId !== groupId || drag.groupDragOver.visual !== visual) {
            drag.setGroupDragOver({ groupId, visual })
        }
    },
    onDragLeave: (event) => {
        if (isLeavingTarget(event)) {
            drag.setGroupDragOver((current) =>
                current?.groupId === groupId && current.visual === visual ? null : current
            )
        }
    },
    onDrop: (event) => {
        event.preventDefault()
        const payload = parseGroupChildPayload(event.dataTransfer.getData(GROUP_CHILD_MIME))
        drag.clear()
        if (!payload || payload.groupId !== groupId || payload.fromVisual === visual) {
            return
        }
        drag.onReorderGroup(groupId, payload.fromVisual, visual)
    },
})

const getRowDropHandlers = (placement: LayerPlacement, drag: LayerRowDrag): DropHandlers | undefined => {
    if (placement.kind === 'top') {
        return getTopDropHandlers(placement.index, drag)
    }
    if (placement.kind === 'child') {
        return getChildDropHandlers(placement.groupId, placement.visual, drag)
    }
    return undefined
}

const LayerDragHandle = ({
    onDragStart,
    onDragEnd,
}: {
    onDragStart: (event: React.DragEvent) => void
    onDragEnd: () => void
}) => (
    <span
        className={styles.layerItemDrag}
        draggable
        onClick={(event) => event.stopPropagation()}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
    >
        <Icon type="sortable_handle" size={12} />
    </span>
)

const LayerActionButton = ({
    active,
    disabled,
    title,
    icon,
    onClick,
}: {
    active: boolean
    disabled?: boolean
    title: string
    icon: string
    onClick: () => void
}) => (
    <button
        type="button"
        className={cn(styles.layerAction, active && styles.layerActionActive)}
        title={title}
        disabled={disabled}
        onClick={(event) => {
            event.stopPropagation()
            onClick()
        }}
    >
        <Icon type={icon} size={12} color="currentColor" />
    </button>
)

export const isParentGroupHidden = (elements: TemplateElement[], groupId: string | null) => {
    if (!groupId) {
        return false
    }
    return Boolean(elements.find((item) => item.id === groupId && item.type === 'group')?.hidden)
}

export const LayerRowItem = ({
    row,
    selected,
    expanded,
    parentHidden,
    drag,
    onSelect,
    onToggleHidden,
    onToggleLock,
    onToggleExpanded,
    onContextMenu,
}: LayerRowItemProps) => {
    const { element, depth } = row
    const placement = getLayerPlacement(row)
    const isGroup = element.type === 'group'
    const effectivelyHidden = Boolean(element.hidden || parentHidden)
    const dropHandlers = getRowDropHandlers(placement, drag)
    const canDrag = placement.kind === 'top' || placement.kind === 'child'

    return (
        <li
            className={cn(
                styles.layerItem,
                selected && styles.layerItemSelected,
                effectivelyHidden && styles.layerItemHidden,
                isRowDragging(placement, drag) && styles.layerItemDragging,
                isRowDropTarget(placement, drag) && styles.layerItemDropTarget
            )}
            style={depth > 0 ? { paddingLeft: 11 + depth * 16 } : undefined}
            onContextMenu={(event) => onContextMenu(element.id, event)}
            onClick={(event) => onSelect(element.id, event.shiftKey)}
            {...dropHandlers}
        >
            {canDrag ? (
                <LayerDragHandle onDragStart={(event) => startRowDrag(placement, drag, event)} onDragEnd={drag.clear} />
            ) : (
                <span className={styles.layerItemDragSpacer} />
            )}
            {isGroup ? (
                <button
                    type="button"
                    className={styles.layerExpand}
                    aria-expanded={expanded}
                    onClick={(event) => {
                        event.stopPropagation()
                        onToggleExpanded(element.id)
                    }}
                >
                    <Icon type={expanded ? 'arrow_down' : 'arrow_right'} size={12} />
                </button>
            ) : (
                <span className={styles.layerExpandSpacer} />
            )}
            <span className={styles.layerItemName}>{element.name}</span>
            <LayerActionButton
                active={effectivelyHidden}
                disabled={parentHidden}
                title={translate('templateEditorToggleVisibility')}
                icon={effectivelyHidden ? 'viewHide' : 'view'}
                onClick={() => onToggleHidden(element.id)}
            />
            <LayerActionButton
                active={Boolean(element.locked)}
                title={translate('templateEditorToggleLock')}
                icon={element.locked ? 'lock' : 'unlock'}
                onClick={() => onToggleLock(element.id)}
            />
        </li>
    )
}
